import React from 'react'
import { Link } from 'react-router-dom'
import clsx from 'clsx'

type LinkButtonProps = {
    type: 'link'
    to: string
}

type ClickButtonProps = {
    type: 'button'
    onClick: () => void
}

type SubmitButtonProps = {
    type: 'submit'
}

type Props = {
    children: React.ReactNode
    style: 'primary' | 'secondary' | 'danger' | 'mild-danger'
    size?: 'normal' | 'small'
    classes?: string
    enabled?: boolean
} & (LinkButtonProps | ClickButtonProps | SubmitButtonProps)

function Button(props: Props): React.JSX.Element {
    const { children, style, size = 'normal', classes, enabled = true } = props

    const baseClasses = clsx(
        classes,
        'text-nowrap rounded-full font-bold transition-colors duration-300 select-none',
        'inline-flex items-center justify-center align-middle text-center',
    )

    const sizeClasses =
        size === 'normal' ? 'py-2 px-4 h-[42px]' : 'py-1 px-2 text-sm h-[30px]'

    // Classes when the button is enabled. They include hover styles.
    const enabledClasses = {
        primary: 'bg-blue-500 text-white hover:bg-blue-600',
        secondary:
            'bg-white text-gray-700 hover:bg-gray-200 border border-gray-300',
        danger: 'bg-red-500 text-white hover:bg-red-600',
        'mild-danger':
            'bg-white text-gray-700 border border-gray-300 hover:text-red-600 hover:border-red-600',
    }

    // Classes to use when disabled: greyed out with no hover effects,
    // and pointer events disabled.
    const disabledClasses =
        'bg-gray-300 text-gray-500 border border-gray-300 cursor-not-allowed opacity-50 pointer-events-none'

    const finalClassName = clsx(
        baseClasses,
        sizeClasses,
        enabled ? enabledClasses[style] : disabledClasses,
    )

    switch (props.type) {
        case 'link':
            return (
                <div>
                    <Link to={props.to} className={finalClassName}>
                        {children}
                    </Link>
                </div>
            )
        case 'button':
            return (
                <div>
                    <button
                        className={finalClassName}
                        onClick={props.onClick}
                        disabled={!enabled}
                    >
                        {children}
                    </button>
                </div>
            )
        case 'submit':
            return (
                <div>
                    <button
                        className={finalClassName}
                        type="submit"
                        disabled={!enabled}
                    >
                        {children}
                    </button>
                </div>
            )
    }
}

export default Button
